import { useParams, Navigate, Link } from "react-router-dom";
import { CheckCircle2, ArrowRight, Calendar } from "lucide-react";

import "../../styles/marketing.css";
import Seo, { breadcrumbJsonLd, faqJsonLd, SITE_URL } from "../../components/marketing/Seo";
import MarketingNav from "../../components/marketing/MarketingNav";
import MarketingFooter from "../../components/marketing/MarketingFooter";
import Breadcrumbs from "../../components/marketing/Breadcrumbs";
import CtaBanner from "../../components/marketing/CtaBanner";
import Reveal from "../../components/marketing/Reveal";
import { BrowserFrame, PhoneFrame } from "../../components/marketing/Frames";
import { getFeature } from "../../data/featuresData";

const HeroMedia = ({ feature }) => {
  if (feature.phoneImgs) {
    return (
      <div className="relative flex justify-center items-start py-4">
        <PhoneFrame src={feature.phoneImgs[0]} alt={feature.imgAlt} className="w-[220px] sm:w-[250px] z-10" />
        {feature.phoneImgs[1] && (
          <PhoneFrame src={feature.phoneImgs[1]} alt={`${feature.name} portal view`} className="w-[190px] sm:w-[220px] -ml-14 mt-12 hidden sm:block" />
        )}
      </div>
    );
  }
  if (feature.iconPanel) {
    return (
      <div className="rounded-3xl bg-gradient-to-tr from-blue-50/80 via-white to-indigo-50/60 border border-slate-200/70 p-8 sm:p-10">
        <div className="grid grid-cols-2 gap-4">
          {feature.iconPanel.map((it) => (
            <div key={it.label} className="rounded-2xl bg-white border border-slate-200 p-6 text-center shadow-[0_10px_24px_-10px_rgba(15,23,42,0.12)]">
              <div className="w-12 h-12 rounded-xl bg-[var(--mk-blue-light)] flex items-center justify-center mx-auto mb-3">
                <it.icon className="w-6 h-6 text-[var(--mk-blue)]" />
              </div>
              <p className="text-[14px] font-semibold text-slate-900">{it.label}</p>
            </div>
          ))}
        </div>
      </div>
    );
  }
  return (
    <div className={`relative ${feature.overlay ? "pb-10 md:pb-14" : ""}`}>
      <div className="rounded-3xl bg-gradient-to-tr from-blue-50/60 via-white to-indigo-50/40 p-3 sm:p-5 border border-slate-200/60">
        <BrowserFrame src={feature.img} alt={feature.imgAlt} eager />
      </div>
      {feature.overlay && (
        <div className="hidden md:block absolute bottom-0 -right-4 lg:-right-8 w-[46%] rounded-xl overflow-hidden border border-slate-200 shadow-[0_20px_40px_-10px_rgba(15,23,42,0.28)] bg-white">
          <img src={feature.overlay} alt={feature.overlayAlt} loading="lazy" width={1440} height={900} className="w-full block" />
        </div>
      )}
    </div>
  );
};

export default function FeaturePage() {
  const { slug } = useParams();
  const feature = getFeature(slug);
  if (!feature) return <Navigate to="/features" replace />;

  const crumbs = [
    { name: "Home", path: "/" },
    { name: "Features", path: "/features" },
    { name: feature.name, path: `/features/${feature.slug}` },
  ];
  const related = feature.related.map(getFeature).filter(Boolean);

  return (
    <div className="mk min-h-screen overflow-x-hidden" data-testid={`feature-page-${feature.slug}`}>
      <Seo
        title={feature.seoTitle}
        description={feature.seoDescription}
        path={`/features/${feature.slug}`}
        image={`${SITE_URL}/marketing/og/og-features.jpg`}
        jsonLd={[breadcrumbJsonLd(crumbs), faqJsonLd(feature.faqs)]}
      />
      <MarketingNav />

      {/* Hero */}
      <section className="pt-24 md:pt-32 pb-14 md:pb-20 bg-gradient-to-b from-blue-50/70 via-white to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <Breadcrumbs items={crumbs} />
          </div>
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div>
              <p className="text-xs uppercase tracking-[0.22em] font-semibold text-[var(--mk-blue)] mb-4">{feature.name}</p>
              <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900 leading-[1.1] mb-5" data-testid="feature-h1">
                {feature.h1}
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed mb-8">{feature.intro}</p>
              <div className="flex flex-col sm:flex-row gap-3.5 mb-5">
                <Link to="/register" className="mk-btn mk-btn-primary mk-btn-lg" data-testid="feature-trial-btn">
                  Start Free for 7 Days
                </Link>
                <Link to="/book-demo" className="mk-btn mk-btn-outline mk-btn-lg" data-testid="feature-demo-btn">
                  <Calendar className="w-4 h-4" /> Book a Demo
                </Link>
              </div>
              <p className="flex items-center gap-1.5 text-sm text-slate-500 font-medium">
                <CheckCircle2 className="w-4 h-4 text-emerald-600" /> No credit card required
              </p>
            </div>
            <HeroMedia feature={feature} />
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {feature.benefits.map((b, i) => (
              <Reveal key={b.title} delay={Math.min(i + 1, 4)}>
                <div className="h-full rounded-2xl border border-slate-200 bg-white p-6 transition-all duration-200 hover:-translate-y-1 hover:shadow-[0_16px_32px_-12px_rgba(15,23,42,0.14)]">
                  <div className="w-11 h-11 rounded-xl bg-[var(--mk-blue-light)] flex items-center justify-center mb-4">
                    <b.icon className="w-5 h-5 text-[var(--mk-blue)]" />
                  </div>
                  <h2 className="text-[16px] font-semibold text-slate-900 mb-2">{b.title}</h2>
                  <p className="text-[14px] text-slate-600 leading-relaxed">{b.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="py-16 md:py-20 bg-[var(--mk-bg-2)] border-y border-slate-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 leading-[1.2]">
              What you can do with {feature.name.toLowerCase()} in Fieldz AI
            </h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 gap-x-10 gap-y-4">
            {feature.bullets.map((b) => (
              <Reveal key={b}>
                <div className="flex items-start gap-3 text-[15px] text-slate-700">
                  <CheckCircle2 className="w-5 h-5 text-[var(--mk-blue)] shrink-0 mt-0.5" />
                  {b}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold tracking-tight text-slate-900 text-center mb-10">
            {feature.name} — frequently asked questions
          </h2>
          <div className="space-y-4">
            {feature.faqs.map((f, i) => (
              <div key={f.q} className="rounded-xl border border-slate-200 bg-white p-5" data-testid={`feature-faq-${i}`}>
                <h3 className="text-slate-900 font-semibold mb-2 text-[15.5px]">{f.q}</h3>
                <p className="text-slate-600 text-[14.5px] leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related features */}
      <section className="py-14 bg-[var(--mk-bg-2)] border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold tracking-tight text-slate-900 mb-8">Works even better with</h2>
          <div className="grid sm:grid-cols-3 gap-5">
            {related.map((r) => (
              <Link
                key={r.slug}
                to={`/features/${r.slug}`}
                className="group rounded-2xl border border-slate-200 bg-white p-6 transition-all duration-200 hover:-translate-y-1 hover:shadow-[0_16px_32px_-12px_rgba(15,23,42,0.14)] hover:border-blue-200"
                data-testid={`related-feature-${r.slug}`}
              >
                <div className="w-10 h-10 rounded-xl bg-[var(--mk-blue-light)] flex items-center justify-center mb-4">
                  <r.icon className="w-5 h-5 text-[var(--mk-blue)]" />
                </div>
                <h3 className="text-[16px] font-semibold text-slate-900 mb-1.5">{r.name}</h3>
                <p className="text-[13.5px] text-slate-500 leading-relaxed mb-3 line-clamp-2">{r.seoDescription}</p>
                <span className="inline-flex items-center gap-1 text-[14px] font-semibold text-[var(--mk-blue)]">
                  Learn more <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CtaBanner title={`Try ${feature.name.toLowerCase()} free for 7 days`} body="Every Fieldz AI plan includes the full platform — no add-ons, no feature gates." />
      <MarketingFooter />
    </div>
  );
}
